import { APIProvider, Map, Marker, useMap } from "@vis.gl/react-google-maps";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import axios from "axios";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GOOGLE_MAPS_API_KEY } from "../../helpers/googleMaps/GoogleMapsLoader";

const DEFAULT_CENTER = { lat: -12.046374, lng: -77.042793 };
const TRACKING_SUBSCRIPTION_ID = "trip";
const CAR_ICON = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="22" fill="#1565c0" stroke="#fff" stroke-width="3"/><path d="M14 25.5 16.2 19a3 3 0 0 1 2.8-2h6a3 3 0 0 1 2.8 2l2.2 6.5v8a1.5 1.5 0 0 1-1.5 1.5h-1a1.5 1.5 0 0 1-1.5-1.5v-1h-9v1a1.5 1.5 0 0 1-1.5 1.5h-1a1.5 1.5 0 0 1-1.5-1.5zm4.5-5.5-1.7 5h14.4l-1.7-5a1 1 0 0 0-.9-.7h-9.2a1 1 0 0 0-.9.7M17 27v3h3v-3zm9 0v3h3v-3z" fill="#fff" transform="translate(-3 -2)"/></svg>',
)}`;
const trackingApi = axios.create({
  baseURL: process.env.REACT_APP_API_NOTIFICATION_URL,
});

function TrackingMarker({ position }) {
  const map = useMap();
  const didCenterOnDriver = useRef(false);

  useEffect(() => {
    if (position && map && !didCenterOnDriver.current) {
      map.panTo(position);
      didCenterOnDriver.current = true;
    }
  }, [map, position]);

  return position ? (
    <Marker position={position} options={{ icon: CAR_ICON }} />
  ) : null;
}

function TrackScreen() {
  const [searchParams] = useSearchParams();
  const driverId = searchParams.get("driverId");
  const orderNumber = searchParams.get("orderNumber");
  const [token] = useState(() => {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    return fragment.get("token") || "";
  });
  const [tracking, setTracking] = useState({ status: "loading", data: null, error: "" });

  useEffect(() => {
    if (window.location.hash.includes("token=")) {
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${window.location.search}`,
      );
    }
  }, []);

  useEffect(() => {
    const parsedDriverId = Number(driverId);
    const parsedOrderNumber = Number(orderNumber);
    if (!token || !Number.isSafeInteger(parsedDriverId) || parsedDriverId <= 0 ||
      !Number.isSafeInteger(parsedOrderNumber) || parsedOrderNumber <= 0) {
      setTracking({ status: "error", data: null, error: "El enlace de seguimiento está incompleto o no es válido." });
      return undefined;
    }

    const controller = new AbortController();
    let disposed = false;
    let trackingEnded = false;
    let socket;
    let reconnectTimer;
    let reconnectDelay = 1000;

    const connectSocket = () => {
      if (disposed || trackingEnded) return;

      const socketUrl = new URL(
        "/api/v1/driver_actives/ws",
        process.env.REACT_APP_API_NOTIFICATION_URL,
      );
      socketUrl.protocol = socketUrl.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(socketUrl.toString());

      socket.onopen = () => {
        socket.send(JSON.stringify({
          type: "subscribe_working",
          subscriptionId: TRACKING_SUBSCRIPTION_ID,
          driverId: parsedDriverId,
          orderNumber: parsedOrderNumber,
          token,
        }));
      };

      socket.onmessage = ({ data }) => {
        let event;
        try {
          event = JSON.parse(data);
        } catch {
          return;
        }

        console.info("[TrackScreen] Mensaje WebSocket de seguimiento", event);
        if (event.subscriptionId && event.subscriptionId !== TRACKING_SUBSCRIPTION_ID) return;

        if (["snapshot", "upsert", "unavailable"].includes(event.type)) {
          reconnectDelay = 1000;
          setTracking({ status: "success", data: event.payload?.data || null, error: "" });
          return;
        }

        if (event.type === "error") {
          const code = event.payload?.error?.code;
          trackingEnded = code === "TRACKING_ENDED";
          setTracking({
            status: "error",
            data: null,
            error: event.payload?.message || "No se pudo actualizar la ubicación del conductor.",
          });
          if (trackingEnded) socket.close();
        }
      };

      socket.onclose = () => {
        if (disposed || trackingEnded) return;
        reconnectTimer = window.setTimeout(connectSocket, reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 15000);
      };

      socket.onerror = () => socket.close();
    };

    setTracking({ status: "loading", data: null, error: "" });
    const trackingPath = `/api/v1/driver_actives/working/${parsedDriverId}`;
    const trackingParams = { orderNumber: parsedOrderNumber };
    console.info("[TrackScreen] Consultando seguimiento REST", {
      url: trackingApi.getUri({ url: trackingPath, params: trackingParams }),
      driverId: parsedDriverId,
      orderNumber: parsedOrderNumber,
    });
    trackingApi
      .get(trackingPath, {
        params: trackingParams,
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
      .then(({ data: response, status }) => {
        console.info("[TrackScreen] Respuesta REST de seguimiento", {
          httpStatus: status,
          body: response,
        });
        setTracking({ status: "success", data: response?.data || null, error: "" });
        connectSocket();
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error("[TrackScreen] Error en consulta REST de seguimiento", {
          httpStatus: error.response?.status,
          body: error.response?.data,
          message: error.message,
        });
        setTracking({
          status: "error",
          data: null,
          error: error.response?.data?.message || "No se pudo consultar el seguimiento. Intenta nuevamente más tarde.",
        });
        connectSocket();
      });

    return () => {
      disposed = true;
      controller.abort();
      window.clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [driverId, orderNumber, token]);

  const position = tracking.data && tracking.data.latitude != null &&
    tracking.data.longitude != null && Number.isFinite(Number(tracking.data.latitude)) &&
    Number.isFinite(Number(tracking.data.longitude))
    ? { lat: Number(tracking.data.latitude), lng: Number(tracking.data.longitude) }
    : null;

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <Box component="main" sx={{ position: "fixed", inset: 0, width: "100vw", height: "100vh" }}>
        <Map
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={13}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          style={{ width: "100%", height: "100%" }}
        >
          <TrackingMarker position={position} />
        </Map>
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            width: "calc(100% - 32px)",
            maxWidth: 420,
            boxSizing: "border-box",
            p: "20px 24px",
            borderRadius: "24px",
            bgcolor: "#222225",
            color: "#fff",
            boxShadow: "0 12px 28px rgba(0, 0, 0, 0.32)",
          }}
        >
          <Typography component="h1" variant="h6" fontWeight={700}>
            Seguimiento de tu servicio
          </Typography>
          {tracking.status === "loading" ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <CircularProgress size={18} sx={{ color: "#18b9e8" }} />
              <Typography sx={{ color: "#d5d5d9" }}>Consultando ubicación…</Typography>
            </Box>
          ) : tracking.status === "success" ? (
            <>
              <Typography sx={{ color: "#d5d5d9" }}>Pedido #{orderNumber}</Typography>
              <Typography sx={{ color: "#d5d5d9" }}>
                {tracking.data?.status === "active" && position
                  ? "Ubicación del conductor actualizada"
                  : "La ubicación del conductor no está disponible por el momento."}
              </Typography>
            </>
          ) : (
            <Typography color="error.main" role="status" sx={{ mt: 1 }}>
              {tracking.error}
            </Typography>
          )}
        </Paper>
        <Paper
          elevation={6}
          sx={{
            position: "absolute",
            left: "50%",
            bottom: 24,
            transform: "translateX(-50%)",
            width: "min(420px, calc(100% - 32px))",
            boxSizing: "border-box",
            p: "22px 24px",
            borderRadius: "24px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            bgcolor: "#222225",
            color: "#fff",
            boxShadow: "0 12px 28px rgba(0, 0, 0, 0.32)",
          }}
        >
          <Box sx={{ pr: 2, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ color: "#a5a6ad", fontWeight: 700, letterSpacing: "0.08em" }}
            >
              Conductor
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25 }}>
              <PersonOutlineRoundedIcon sx={{ color: "#18b9e8", fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: "#f7f7f8", fontWeight: 600 }} noWrap>
                Nombre
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
              <PhoneOutlinedIcon sx={{ color: "#18b9e8", fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: "#d5d5d9" }} noWrap>
                Teléfono
              </Typography>
            </Box>
          </Box>
          <Box sx={{ pl: 2.5, minWidth: 0, borderLeft: "1px solid #39393f" }}>
            <Typography
              variant="caption"
              sx={{ color: "#a5a6ad", fontWeight: 700, letterSpacing: "0.08em" }}
            >
              Vehículo
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25 }}>
              <DirectionsCarOutlinedIcon sx={{ color: "#18b9e8", fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: "#f7f7f8", fontWeight: 700 }} noWrap>
                Placa
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
              <Box sx={{ width: 20, flex: "0 0 20px" }} />
              <Typography variant="body2" sx={{ color: "#d5d5d9" }} noWrap>
                Modelo
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </APIProvider>
  );
}

export default TrackScreen;
